import buffer from '@turf/buffer';
import convex from '@turf/convex';
import { featureCollection, point } from '@turf/helpers';

const MIN_COORD_DELTA = 1e-9;
const MAX_ENVELOPE_POINTS_PER_WORK = 2500;
/** Margem extra além da largura do traçado, para o envelope ficar por fora do desenho. */
const ENVELOPE_PADDING_METERS = 2;

const toLineCoordinates = (path) => {
    if (!Array.isArray(path) || path.length < 2) {
        return null;
    }

    const coordinates = [];
    for (let i = 0; i < path.length; i++) {
        const p = path[i];
        if (p?.lat == null || p?.lng == null || isNaN(p.lat) || isNaN(p.lng)) {
            continue;
        }
        const lng = Number(p.lng);
        const lat = Number(p.lat);
        if (coordinates.length > 0) {
            const [prevLng, prevLat] = coordinates[coordinates.length - 1];
            if (
                Math.abs(prevLng - lng) < MIN_COORD_DELTA &&
                Math.abs(prevLat - lat) < MIN_COORD_DELTA
            ) {
                continue;
            }
        }
        coordinates.push([lng, lat]);
    }

    return coordinates.length >= 2 ? coordinates : null;
};

const ringToLatLngPath = (ring) => {
    if (!Array.isArray(ring) || ring.length < 3) {
        return null;
    }
    return ring.map(([lng, lat]) => ({ lat, lng }));
};

/**
 * Converte path {lat,lng}[] em anéis LatLng do buffer Turf (largura total do implemento).
 */
export const buildCorridorPathsWithTurf = (path, widthMeters) => {
    if (!(widthMeters > 0)) {
        return null;
    }

    const coordinates = toLineCoordinates(path);
    if (!coordinates) {
        return null;
    }

    let buffered;
    try {
        buffered = buffer(
            {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates,
                },
            },
            widthMeters / 2,
            {
                units: 'meters',
                steps: 4,
            }
        );
    } catch (err) {
        return null;
    }

    if (!buffered?.geometry?.coordinates) {
        return null;
    }

    const { type, coordinates: geomCoords } = buffered.geometry;

    if (type === 'Polygon') {
        const outer = ringToLatLngPath(geomCoords[0]);
        return outer ? [outer] : null;
    }

    if (type === 'MultiPolygon') {
        const paths = [];
        for (const poly of geomCoords) {
            const outer = ringToLatLngPath(poly?.[0]);
            if (outer) {
                paths.push(outer);
            }
        }
        return paths.length > 0 ? paths : null;
    }

    return null;
};

const getWorkKey = (linha) =>
    linha.trabalhoId ?? linha.workLabel ?? linha.borderColor;

const collectWorkPoints = (items) => {
    const points = [];
    for (const linha of items) {
        const coords = toLineCoordinates(linha.path);
        if (!coords) {
            continue;
        }
        for (const [lng, lat] of coords) {
            points.push(point([lng, lat]));
        }
    }
    return points;
};

const samplePoints = (points, maxPoints) => {
    if (!Array.isArray(points) || points.length <= maxPoints) {
        return points;
    }
    const step = Math.ceil(points.length / maxPoints);
    return points.filter((_, index) => index % step === 0);
};

const resolveWorkWidthMeters = (items, fallbackWidthMeters) => {
    let maxWidth = fallbackWidthMeters > 0 ? fallbackWidthMeters : 0;
    for (const linha of items) {
        if (linha.strokeWidthMeters > 0) {
            maxWidth = Math.max(maxWidth, linha.strokeWidthMeters);
        }
    }
    return maxWidth > 0 ? maxWidth : null;
};

/**
 * Envelope (convex hull) dos pontos do trabalho, com margem pela largura do traçado.
 * Mais limpo que union de corredores — ideal para testar identificação multi-trabalho.
 *
 * @returns {{ id, label, color, rings: {lat,lng}[][] }[]}
 */
export const buildWorkBorderOutlines = (linhas, fallbackWidthMeters = null) => {
    if (!Array.isArray(linhas) || linhas.length === 0) {
        return [];
    }

    const byWork = new Map();

    for (const linha of linhas) {
        if (!linha || linha.thinLine || !linha.borderColor || !Array.isArray(linha.path) || linha.path.length < 2) {
            continue;
        }
        const key = getWorkKey(linha);
        if (!byWork.has(key)) {
            byWork.set(key, {
                id: linha.trabalhoId ?? key,
                label: linha.workLabel || (linha.trabalhoId != null ? `#${linha.trabalhoId}` : 'Trabalho'),
                color: linha.borderColor,
                items: [],
            });
        }
        byWork.get(key).items.push(linha);
    }

    const outlines = [];

    for (const work of byWork.values()) {
        let points = collectWorkPoints(work.items);
        if (points.length < 3) {
            continue;
        }

        points = samplePoints(points, MAX_ENVELOPE_POINTS_PER_WORK);
        if (points.length < 3) {
            continue;
        }

        let hull;
        try {
            hull = convex(featureCollection(points));
        } catch (err) {
            hull = null;
        }
        if (!hull?.geometry) {
            continue;
        }

        // Expande o hull pela meia largura do implemento + padding, para ficar por fora do traçado.
        const widthMeters = resolveWorkWidthMeters(work.items, fallbackWidthMeters);
        const expandMeters = (widthMeters > 0 ? widthMeters / 2 : 0) + ENVELOPE_PADDING_METERS;
        let outlineFeature = hull;
        if (expandMeters > 0) {
            try {
                const expanded = buffer(hull, expandMeters, { units: 'meters', steps: 8 });
                if (expanded?.geometry) {
                    outlineFeature = expanded;
                }
            } catch (err) {
                // mantém hull original
            }
        }

        const ring = ringToLatLngPath(
            outlineFeature.geometry.type === 'Polygon'
                ? outlineFeature.geometry.coordinates?.[0]
                : outlineFeature.geometry.coordinates?.[0]?.[0]
        );
        if (!ring) {
            continue;
        }

        outlines.push({
            id: work.id,
            label: work.label,
            color: work.color,
            rings: [ring],
        });
    }

    return outlines;
};

export const __testables = {
    toLineCoordinates,
    collectWorkPoints,
    samplePoints,
    resolveWorkWidthMeters,
};
